import React, { useState } from "react";
import { Alert, Button, Divider, message, Modal, Rate, Tag, Typography } from "antd";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import api from "Common/Client/Interceptor";
import styled, { keyframes } from "styled-components";
import TextArea from "antd/es/input/TextArea";

const { Title, Text, Paragraph } = Typography;

const fallDown = keyframes`
  0% {
    transform: translateY(-100%);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

// Styled component for the animated alert
const FallingAlert = styled(Alert)`
  animation: ${fallDown} 0.5s ease-out;
  margin-bottom: 16px;
  padding: 8px 15px;
`;

const TrainingIntro = ({ projectData, fetchProjects, setGroupMemberId, groupMemberId }) => {
  const projectTimeline =
    projectData?.startDate && projectData?.endDate
      ? `${new Date(projectData.startDate).toLocaleDateString()} - ${new Date(
          projectData.endDate
        ).toLocaleDateString()}`
      : "N/A";

  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState("");

  const handleSubmitRating = async () => {
    if (!rating) {
      message.warning("Please select a rating before submitting");
      return;
    }

    if (!feedback.trim()) {
      message.warning("Please provide your feedback before submitting");
      return;
    }

    try {
      setSubmitting(true);
      await api.post(
        "/TrainingGroup/Learner/Submit-Rating",
        {
          groupMemberId: groupMemberId,
          rating: rating,
          feedBack: feedback,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      message.success("Thank you for submitting your rating and feedback!");
      setShowRatingModal(false);
      setFeedback("");
      setGroupMemberId(null);
      fetchProjects();
    } catch (error) {
      console.error("Failed to submit rating:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewCourseItems = () => {
    navigate("/course-items", {
      state: {
        courseFiles: projectData?.course?.courseFiles,
        courseTitle: projectData?.title,
        courseDescription: projectData?.description,
      },
    });
  };

  return (
    <>
      {projectData?.group?.ownSubmitedRating === null &&
        projectData?.group?.ownSubmitedFeedBack === null && (
          <FallingAlert
            message="Rate your experience"
            description="We would appreciate your feedback about this training for your
          mentor. Please take a moment to rate your experience."
            type="info"
            showIcon
            action={
              <Button type="primary" onClick={() => setShowRatingModal(true)}>
                Provide Rating
              </Button>
            }
            style={{ marginBottom: 20 }}
          />
        )}
      <Title level={4} style={{ marginBottom: "10px" }}>
        {projectData?.title || "N/A"}
      </Title>

      <Text style={{ display: "block", marginBottom: "10px" }}>
        <strong>Description:</strong> {projectData?.description || "N/A"}
      </Text>

      <Text style={{ display: "block", marginBottom: "10px" }}>
        <strong>Assigned Group:</strong> {projectData?.groupName || "N/A"}
      </Text>

      <Text style={{ display: "block", marginBottom: "10px" }}>
        <strong>Timeline:</strong> {projectTimeline}
      </Text>
      {projectData && (
        <>
          {/* Display Mentor Rating */}
          <Text strong>Your Rating for Mentor:</Text>{" "}
          {projectData.group?.members?.[0]?.rating ? (
            <Rate disabled value={projectData.group.members[0].rating} />
          ) : (
            <Text type="secondary">
              Your mentor has not been rated yet. Please provide your feedback
            </Text>
          )}
          <br />
        </>
      )}
      <Tag style={{ backgroundColor: "orange", color: "white", marginTop: 16 }}>
        {projectData?.isRetryTraining ? "Repeated" : ""}
      </Tag>
      <br />
      <Button type="primary" onClick={handleViewCourseItems} style={{ marginTop: 16 }}>
        View Course Items
      </Button>
      <Modal
        title="Rate Your Experience with the Training for Your Mentor"
        open={showRatingModal}
        onOk={handleSubmitRating}
        onCancel={() => {
          setShowRatingModal(false);
          setFeedback(""); // Reset feedback when modal is closed
        }}
        okText="Submit Rating & Feedback"
        cancelText="Cancel"
        centered
        confirmLoading={submitting}
      >
        <div style={{ textAlign: "center", margin: "20px 0" }}>
          <Paragraph>
            How would you rate your overall experience with this training, specifically for your
            mentor?
          </Paragraph>
          <Rate value={rating} onChange={setRating} style={{ fontSize: 32 }} />

          <Divider />

          <Paragraph>Please provide your feedback about the training:</Paragraph>
          <TextArea
            rows={4}
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Your feedback is important to us..."
            required
            style={{ marginBottom: 16, width: "100%" }}
          />
        </div>
      </Modal>
    </>
  );
};

TrainingIntro.propTypes = {
  projectData: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    isRetryTraining: PropTypes.bool,
    groupName: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    course: PropTypes.shape({
      id:PropTypes.number,
      courseFiles: PropTypes.arrayOf(PropTypes.object), // assuming courseFiles is array of file objects
    }),
    group: PropTypes.shape({
      ownSubmitedRating: PropTypes.number,
      ownSubmitedFeedBack: PropTypes.string,
      members: PropTypes.arrayOf(
        PropTypes.shape({
          rating: PropTypes.number,
        })
      ),
    }),
  }).isRequired,
  fetchProjects: PropTypes.func.isRequired,
  setGroupMemberId: PropTypes.func.isRequired,
  groupMemberId: PropTypes.number,
};

export default TrainingIntro;
